import React, { useState, useEffect, useContext } from 'react'
import styled, { css } from 'styled-components'
import { connect } from 'react-redux'
import { isNumber } from 'lodash'
import { giphyWhite } from '@giphy/js-brand'
import Gif from './gif'
import Sidebar from './sidebar'
import NextPrev from './next-prev'
import { fitGifToSize } from 'shared/util/sizing'
import log from 'shared/util/log'
import getViewCountDisplay from 'shared/util/view-count-formatter'
import * as selectors from 'shared/redux/reducers'
import CTA from 'shared/components/cta'
import { fetchGifDetailViewCount } from 'shared/api/index'
import { Actions } from 'shared/util/gif-detail-ga-events'
import { PaginationButton } from 'shared/css/buttons'
import { SectionHeader } from 'shared/components/text-elements'
import GifDetailOverlay from './overlays'
import CollectionsMessage from 'shared/components/collections/collections-message'
import { IVideo } from 'shared/types/video'
import { isEmpty } from 'lodash'
import Tags from 'shared/components/tags/tags'
import { CollectionContext } from 'shared/contexts/add-to-collections-manager'

const GIF_WIDTH = 500
const GIF_HEIGHT = 573
const MINIMUM_HEIGHT = 250

const Container = styled.div<{ isPrivate: boolean }>`
    color: ${giphyWhite};
    padding-top: 24px;
    position: relative;
    text-align: left;
    &:after {
        clear: both;
        content: '';
        display: block;
    }
    margin-bottom: ${({ isPrivate }) => isPrivate && `64px`};
`

const ContentWrapper = styled.div`
    float: left;
    margin: 0 0 16px;
    position: relative;
    width: ${GIF_WIDTH};
    vertical-align: top;
`

const Content = styled.div`
    position: relative;
    input[type='text'] {
        box-sizing: border-box;
        cursor: text;
        height: 30px;
        line-height: 30px;
    }
`

const Navigation = styled.div<{ gifHeight: number }>`
    float: right;
    margin-bottom: 16px;
    vertical-align: top;
    width: 243px;
    height: ${({ gifHeight }) => `${gifHeight + 36}px`};
    display: flex;
    flex-direction: column;
`

const Header = styled.div`
    height: 26px;
`

const ViewCountContainer = styled(SectionHeader)`
    margin-top: auto;
    margin-bottom: 3px;
    margin-left: 5px;
    color: #b2b2b2;
    font-size: 14px;
    font-weight: bold;
    text-transform: none;
    &:before {
        display: inline-block;
        font-size: 16px;
        margin-right: 8px;
        vertical-align: top;
    }
`

const CTAContainer = styled.div`
    margin-top: 50px;
`

const TagsContainer = styled.div<{ isSmall: boolean }>`
    ${({ isSmall }) =>
        isSmall &&
        css`
            width: ${GIF_WIDTH}px;
        `}

    div {
        max-height: auto;
    }
`

const EMBED_MIN_REQUIRED_WIDTH = 500
const EMBED_MIN_REQUIRED_HEIGHT = 330

type Props = {
    gif: IVideo
    prevGif: IVideo
    nextGif: IVideo
    children: any
    gifId: string
    groupId: string
    tags: []
    isSticker: boolean
}

const GifDetailGif = ({ gif, prevGif, nextGif, children, tags = [], isSticker }: Props) => {
    const [views, setViews] = useState<number>(0)
    const [overlayType, setOverlayType] = useState<string | null>(null)
    const { addGifToCollectionSuccessMessage, addGifToMultipleCollectionsSuccessMessage } = useContext(
        CollectionContext
    )

    useEffect(() => {
        setViews(0)
        fetchAnalytics(gif.id)
    }, [gif.id])

    const fetchAnalytics = async (id: string | number) => {
        try {
            const { viewCount } = await fetchGifDetailViewCount(id)
            !!viewCount && setViews(viewCount)
        } catch (e) {
            log.error(e)
        }
    }

    const fitToSize = fitGifToSize(gif, GIF_WIDTH, GIF_HEIGHT)

    const gifSize = {
        ...{
            ...(overlayType === 'embed' && fitToSize.height <= EMBED_MIN_REQUIRED_HEIGHT
                ? {
                      width: EMBED_MIN_REQUIRED_WIDTH,
                      height: EMBED_MIN_REQUIRED_HEIGHT,
                  }
                : fitToSize),
        },
        left: 0,
        top: 0,
    }

    const { text, link } = gif.cta || { text: '', link: '' }

    return (
        <>
            <Container isPrivate={gif.is_hidden}>
                <ContentWrapper>
                    <Content>
                        {overlayType ? (
                            <>
                                <Gif
                                    {...{
                                        gif,
                                        gifSize,
                                    }}
                                />
                                <GifDetailOverlay
                                    gif={gif}
                                    height={gifSize.height}
                                    overlayType={overlayType}
                                    closeOverlay={setOverlayType}
                                />
                            </>
                        ) : (
                            <>
                                <Gif
                                    {...{
                                        gif,
                                        gifSize,
                                    }}
                                />
                                {(addGifToCollectionSuccessMessage || addGifToMultipleCollectionsSuccessMessage) && (
                                    <CollectionsMessage top={26} width={GIF_WIDTH} height={gifSize.height} showGif />
                                )}
                            </>
                        )}
                        {children}
                    </Content>
                    {text && link && (
                        <CTAContainer>
                            <CTA gif={gif} onClick={() => {}} width={GIF_WIDTH} />
                        </CTAContainer>
                    )}
                </ContentWrapper>
                <Navigation gifHeight={gifSize.height}>
                    <Header style={{ textAlign: `right` }}>
                        <NextPrev gif={prevGif} gaAction={Actions.previousClick}>
                            <PaginationButton className={'ss-navigateleft'} />
                        </NextPrev>
                        <NextPrev gif={nextGif} gaAction={Actions.nextClick}>
                            <PaginationButton className={'ss-navigateright'} style={{ marginLeft: `3px` }} />
                        </NextPrev>
                    </Header>
                    <Sidebar gif={gif} setOverlayType={setOverlayType} />
                    {isNumber(views) && views > 0 && (
                        <ViewCountContainer className={'ss-view'}>{getViewCountDisplay(views)}</ViewCountContainer>
                    )}
                </Navigation>
            </Container>
            {!isEmpty(tags) && (
                <TagsContainer isSmall={!!(fitToSize.height < MINIMUM_HEIGHT)}>
                    <Tags tags={tags} isSticker={isSticker} />
                </TagsContainer>
            )}
        </>
    )
}

export default connect((state, { gifId, groupId }: Props) => ({
    gif: selectors.getGifById(state, gifId),
    prevGif: selectors.getPrevGif(state, gifId, groupId),
    nextGif: selectors.getNextGif(state, gifId, groupId),
}))(GifDetailGif)
